import { Controller } from "@hotwired/stimulus"

// Mixpanel events for API Key Created / Revoked fire server-side from
// ApiKeysController — no client-side tracking here to avoid double-counting.

export default class extends Controller {
  static targets = [
    "rows", "row", "emptyState", "table",
    "createModal", "createForm", "createSubmit",
    "revealModal", "revealInput", "copyButton", "copyLabel",
    "revokeModal", "revokeName", "revokeConfirm"
  ]

  static values = {
    createUrl: String,
    destroyUrlTemplate: String
  }

  connect() {
    this._pendingRevokeId = null
  }

  // ── Modal plumbing ────────────────────────────────────────────

  openCreateModal() {
    this.createModalTarget.classList.remove("hidden")
    const nameInput = this.createFormTarget.querySelector("input[name='api_key[name]']")
    setTimeout(() => nameInput?.focus(), 50)
  }

  closeCreateModal() {
    this.createModalTarget.classList.add("hidden")
    this.createFormTarget.reset()
  }

  closeRevealModal() {
    this.revealModalTarget.classList.add("hidden")
    this.revealInputTarget.value = ""
  }

  openRevokeModal(event) {
    this._pendingRevokeId = event.params.id
    this.revokeNameTarget.textContent = event.params.name
    this.revokeModalTarget.classList.remove("hidden")
  }

  closeRevokeModal() {
    this.revokeModalTarget.classList.add("hidden")
    this._pendingRevokeId = null
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Create flow ───────────────────────────────────────────────

  async submitCreate(event) {
    event.preventDefault()
    const form = this.createFormTarget
    const data = new FormData(form)

    this.createSubmitTarget.disabled = true
    this.createSubmitTarget.textContent = "Generating…"

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body: data
      })
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || "Failed to create API key")
      }
      const json = await response.json()

      // Insert the new row at the top of the table (or replace the empty state)
      this._insertOrReplaceRow(json.id, json.row_html)

      // Show reveal modal with the one-time token
      this.closeCreateModal()
      this.revealInputTarget.value = json.plaintext_token
      this.revealModalTarget.classList.remove("hidden")
      this._resetCopyButton()
    } catch (e) {
      this._showToast(e.message || "Failed to create API key", "error")
    } finally {
      this.createSubmitTarget.disabled = false
      this.createSubmitTarget.textContent = "Generate key"
    }
  }

  // ── Copy-to-clipboard ─────────────────────────────────────────

  async copyKey() {
    const value = this.revealInputTarget.value
    try {
      await navigator.clipboard.writeText(value)
      this.copyLabelTarget.textContent = "Copied!"
      this.copyButtonTarget.classList.add("bg-green-600", "hover:bg-green-700")
      this.copyButtonTarget.classList.remove("bg-brand", "hover:bg-brand-dark")
      setTimeout(() => this._resetCopyButton(), 1500)
    } catch {
      // Fallback: select the input so the user can Cmd+C
      this.revealInputTarget.select()
      this._showToast("Press Cmd+C to copy", "info")
    }
  }

  // ── Revoke flow ───────────────────────────────────────────────

  async confirmRevoke() {
    const id = this._pendingRevokeId
    if (!id) return

    this.revokeConfirmTarget.disabled = true
    this.revokeConfirmTarget.textContent = "Revoking…"

    try {
      const url = this.destroyUrlTemplateValue.replace("ID", id)
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      if (!response.ok) throw new Error("Failed to revoke key")
      const json = await response.json()

      this._insertOrReplaceRow(json.id, json.row_html)
      this.closeRevokeModal()
      this._showToast("API key revoked")
    } catch (e) {
      this._showToast(e.message || "Failed to revoke key", "error")
    } finally {
      this.revokeConfirmTarget.disabled = false
      this.revokeConfirmTarget.textContent = "Revoke key"
    }
  }

  // ── Helpers ───────────────────────────────────────────────────

  _insertOrReplaceRow(id, html) {
    const existing = this.rowsTarget.querySelector(`tr[data-api-key-id="${id}"]`)
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const newRow = template.content.firstElementChild

    if (existing) {
      existing.replaceWith(newRow)
    } else {
      this.rowsTarget.prepend(newRow)
    }

    // Swap the empty-state card for the table on first insert.
    if (this.hasEmptyStateTarget && !this.emptyStateTarget.classList.contains("hidden")) {
      this.emptyStateTarget.classList.add("hidden")
      this.tableTarget.classList.remove("hidden")
    }
  }

  _resetCopyButton() {
    this.copyLabelTarget.textContent = "Copy"
    this.copyButtonTarget.classList.remove("bg-green-600", "hover:bg-green-700")
    this.copyButtonTarget.classList.add("bg-brand", "hover:bg-brand-dark")
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-[13px]"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
